// TableDateFormatter.tsx
import React, { useState, useEffect } from 'react';
import { format, isToday, isYesterday, isThisWeek, isThisYear, differenceInSeconds } from 'date-fns';
import { es } from 'date-fns/locale';

interface TableDateFormatterProps {
  data: string;
  stopCounting: boolean;
  isTimestamp: boolean;
  isRealTime: boolean;
}

const TableDateFormatter: React.FC<TableDateFormatterProps> = ({ data, stopCounting, isTimestamp, isRealTime }) => {
  const [timeLeft, setTimeLeft] = useState('');
  const date = new Date(data);

  useEffect(() => {
    if (isRealTime && !stopCounting) {
      const timer = setInterval(() => {
        updateRealTime();
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [data, isRealTime, stopCounting]);

  const formatTime = (date: Date) => {
    const timeFormat = "HH:mm";
    if (isToday(date)) {
      return format(date, timeFormat);
    } else if (isYesterday(date)) {
      return `Ayer, ${format(date, timeFormat)}`;
    } else if (isThisWeek(date)) {
      return `${format(date, 'EEEE', { locale: es })}, ${format(date, timeFormat)}`;
    } else if (isThisYear(date)) {
      return format(date, "dd 'de' MMMM", { locale: es });
    } else {
      return format(date, "dd 'de' MMMM, yyyy", { locale: es });
    }
  };

  const updateRealTime = () => {
    const now = new Date();
    const secondsDiff = differenceInSeconds(date, now);
    if (secondsDiff > 0) {
      const hours = Math.floor(secondsDiff / 3600);
      const minutes = Math.floor((secondsDiff % 3600) / 60);
      const seconds = secondsDiff % 60;
      setTimeLeft(`${hours}h ${minutes}m ${seconds}s remaining`);
    } else if (secondsDiff < 0) {
      if (!stopCounting) {
        const hours = Math.abs(Math.floor(secondsDiff / 3600));
        const minutes = Math.abs(Math.floor((secondsDiff % 3600) / 60));
        const seconds = Math.abs(secondsDiff % 60);
        setTimeLeft(`${hours}h ${minutes}m ${seconds}s tarde`);
      } else {
        setTimeLeft(formatTime(date));
      }
    } else {
      setTimeLeft('Now');
    }
  };

  const stateClass = isRealTime
    ? differenceInSeconds(new Date(), date) > 0
      ? 'text-red-700'
      : 'text-[#FC7556]'
    : '';

  return (
    <span className={`text-sm tabular-nums whitespace-nowrap ${stateClass}`} style={!isRealTime ? { color: 'var(--text-color)' } : undefined}>
      {isRealTime ? timeLeft : formatTime(date)}
    </span>
  );
};

export default TableDateFormatter;
